import { generatePresignedUrl1 } from "./aws";

export const AWS_S3_URL =
  "https://nirantara-assets-private.s3.ap-south-1.amazonaws.com";
// "https://nirantara-assets.s3.ap-south-1.amazonaws.com";

export const getImageUrl = async (fileUrl) => {
  if (!fileUrl) return ""; // handle empty values
  if (fileUrl.startsWith("http://") || fileUrl.startsWith("https://")) {
    return fileUrl;
  }
  let fileName = `${AWS_S3_URL}/${fileUrl}`;
  const presignerUrl = await generatePresignedUrl1("private", fileName);
  return presignerUrl;
};

export function capitalizeFirstLetter(str) {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}

export const validateFile = (file, allowedTypes, maxFileSize) => {
  if (!allowedTypes.includes(file.type)) {
    return `File type not allowed: ${file.name}`;
  }
  if (file.size > maxFileSize) {
    return `File size exceeds 5MB: ${file.name}`;
  }
  return null; // No errors
};

export const checkImageDimensions = (file, maxWidth, maxHeight) => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    const fileURL = URL.createObjectURL(file);
    image.src = fileURL;

    image.onload = () => {
      URL.revokeObjectURL(fileURL); // Release memory
      if (image.width > maxWidth || image.height > maxHeight) {
        reject(new Error(`Image dimensions exceed 1200x800: ${file.name}`));
      } else {
        resolve();
      }
    };

    image.onerror = () => {
      URL.revokeObjectURL(fileURL); // Release memory
      reject(new Error(`Unable to load image: ${file.name}`));
    };
  });
};

// export const fixedTons = (value) => {
//   return Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(3);
// };

// export const fixedPercentage = (value) => {
//   return Number(value) % 1 === 0 ? Number(value) : Number(value).toFixed(3);
// };

export const fixedTons = (value) => {
  const num = Number(value || 0);
  return num % 1 === 0 ? num : parseFloat(num.toFixed(3));
};

export const fixedPercentage = (value) => {
  const num = Number(value);
  return num % 1 === 0 ? num : parseFloat(num.toFixed(2));
};

export const generateYearList = (startYear = 2013) => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = startYear; year <= currentYear; year++) {
    years.push(year);
  }
  return years;
};

export const formatNumber = (value) => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "M"; // Convert to Million
  } else if (value >= 100000) {
    return (value / 100000).toFixed(1).replace(/\.0$/, "") + "L"; // Convert to Lakh
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K"; // Convert to Thousand
  }
  return value.toString(); // Return original number if less than 1000
};

export const truncateEmissionNumber = (value, decimals = 3) => {
  const num = Number(value);

  if (!Number.isFinite(num)) return "0";

  const factor = Math.pow(10, decimals);

  // Round to 3 decimal places
  const rounded = Math.round((num + Number.EPSILON) * factor) / factor;

  return rounded.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: decimals,
  });
};

export const truncateNumber = (value, decimals = 3) => {
  if (value == null || isNaN(value)) return "0";
  // console.log("inside", value);

  const factor = Math.pow(10, decimals);
  const truncated = Math.trunc(value * factor) / factor;

  const [intPart, decimalPart] = truncated.toString().split(".");
  const formattedInt = Number(intPart).toLocaleString("en-US");
  return decimalPart ? `${formattedInt}.${decimalPart}` : formattedInt;
};

/** e.g. 1.77E-02, 1.11E-06 (uppercase E, 2-digit exponent when |exp| < 10) */
export const formatNumberScientificCompact = (value, mantissaDigits = 2) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (n === 0) return "0";
  const str = n.toExponential(mantissaDigits);
  const m = str.match(/^([+-]?\d+(?:\.\d+)?)e([+-])(\d+)$/i);
  if (!m) return str.replace(/e/gi, "E");
  const [, mantissa, sign, expStr] = m;
  const expNum = (sign === "-" ? -1 : 1) * parseInt(expStr, 10);
  const expAbs = Math.abs(expNum);
  const expBody =
    expAbs < 10 ? String(expAbs).padStart(2, "0") : String(expAbs);
  return `${mantissa}E${expNum < 0 ? "-" : "+"}${expBody}`;
};

const SMALL_EMISSION_DISPLAY_THRESHOLD = 0.1;

/** Truncate toward zero to `decimals` fractional digits; integers stay un-suffixed, else fixed width (e.g. 0.190589 → 0.190). */
export const formatTruncatedDecimalPlaces = (value, decimals = 3) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (n === 0) return "0";
  const factor = Math.pow(10, decimals);
  const t = Math.trunc(n * factor) / factor;
  if (t === 0) return "0";
  const nearestInt = Math.trunc(t);
  if (Math.abs(t - nearestInt) < 1e-12) {
    return nearestInt.toLocaleString("en-US");
  }
  const s = t.toFixed(decimals);
  const m = s.match(/^(-?)(\d+)\.(\d+)$/);
  if (!m) return s;
  const [, sign, intRaw, frac] = m;
  const formattedInt = Number(intRaw).toLocaleString("en-US");
  return `${sign}${formattedInt}.${frac}`;
};

/** Truncated display (default 3 decimals); scientific when 0 < |n| < 0.1 */
export const formatDashboardEmissionValue = (value, decimals = 3) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "0";
  if (n === 0) return "0";
  const abs = Math.abs(n);
  if (abs > 0 && abs < SMALL_EMISSION_DISPLAY_THRESHOLD) {
    return formatNumberScientificCompact(fixedTons(n, 2));
  }
  return formatTruncatedDecimalPlaces(fixedTons(n, decimals));
};

export const formatNumberCommaSeparated = (num) => {
  if (isNaN(num)) return num;

  const numericValue = Number(num);
  const normalizedNum =
    numericValue % 1 !== 0 && num.toString().split(".")?.[1]?.length > 3
      ? numericValue.toFixed(3)
      : num;

  const [intPart, decimalPart] = normalizedNum.toString().split(".");
  const formattedInt = Number(intPart).toLocaleString("en-US");
  return decimalPart ? `${formattedInt}.${decimalPart}` : formattedInt;
};

export const formatNumberCommaSeparatedText = (input) => {
  if (input == null || input === "") return input;
  const inputStr = input.toString();
  const match = inputStr.match(/^([-+]?\d*\.?\d+)([a-zA-Z%]*)$/);
  if (!match) return inputStr;
  const [, numberPart, suffix] = match;
  const [intPart, decimalPart] = numberPart.split(".");
  const formattedInt = Number(intPart).toLocaleString("en-US");
  const formattedNumber = decimalPart
    ? `${formattedInt}.${decimalPart}`
    : formattedInt;
  return `${formattedNumber}${suffix}`;
};

/** Remove visual formatting (commas/spaces) from numeric text input. */
export const stripNumberFormatting = (value) => {
  if (value == null) return "";
  return String(value).replace(/,/g, "").trim();
};

/**
 * Format a number-like text input with commas (e.g. "20000" -> "20,000").
 * Keeps a single leading '-' and a single decimal point if present.
 */
export const formatNumberInputWithCommas = (value) => {
  const raw = stripNumberFormatting(value);
  if (raw === "") return "";
  // Keep digits, one leading '-', and one '.'
  const sign = raw.startsWith("-") ? "-" : "";
  const unsigned = raw.replace(/^-/, "");
  const cleaned = unsigned.replace(/[^\d.]/g, "");
  const firstDot = cleaned.indexOf(".");
  const normalized =
    firstDot === -1
      ? cleaned
      : `${cleaned.slice(0, firstDot)}.${cleaned
        .slice(firstDot + 1)
        .replace(/\./g, "")}`;
  return sign + formatNumberCommaSeparatedText(normalized);
};

export const parseAddressComponents = (name, address_components = []) => {
  let street = "";
  let city = "";
  let state = "";
  let country = "";
  let pincode = "";
  address_components.forEach((component) => {
    if (name) {
      street = name;
    }
    if (component.types.includes("administrative_area_level_3")) {
      city = component.long_name;
    }
    if (component.types.includes("administrative_area_level_1")) {
      state = component.long_name;
    }
    if (component.types.includes("country")) {
      country = component.long_name;
    }
    if (component.types.includes("postal_code")) {
      pincode = component.long_name;
    }
  });
  const addressPartsRaw = [street, city, state, country, pincode];
  // Remove falsy and duplicate values (case-insensitive)
  const seen = new Set();
  const addressParts = addressPartsRaw?.filter((part) => {
    if (!part) return false;
    const key = part.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  return addressParts.join(", ");
};

export const isFilled = (field) =>
  field &&
  field.value !== undefined &&
  field.value !== null &&
  field.value !== "" &&
  typeof field.unit === "string" &&
  field.unit.trim() !== "";

export const isFilledValue = (field) =>
  field &&
  field.value !== undefined &&
  field.value !== null &&
  field.value !== "";

export const checkUserRoles = (user) => {
  const {
    companyAdmin,
    facilityManger, // ← using your existing schema spelling
    groupManager,
    subGroupManager,
    isSubCompanyAdmin,
  } = user || {};

  return {
    isCompanyAdmin:
      facilityManger || groupManager || subGroupManager || isSubCompanyAdmin,
    hasAnyRole:
      companyAdmin ||
      facilityManger ||
      groupManager ||
      subGroupManager ||
      isSubCompanyAdmin,
  };
};
